<main class="page">
  <!-- Hero -->
  <header class="hero" id="hero">
    <!-- Particle layer -->

    <div class="hero-inner">
      <p class="hero-kicker">Full-Stack Web &amp; Mobile Developer</p>

      <h1 class="hero-title">Michael Artman</h1>

      <p class="hero-subtitle">
        I build production-ready systems that connect web, mobile, hardware, and
        data — especially in environments where reliability actually matters.
      </p>

      <div class="hero-tags">
        <span class="hero-tag">Angular &amp; Material</span>
        <span class="hero-tag">Ionic / Capacitor</span>
        <span class="hero-tag">Python &amp; PostgreSQL</span>
        <span class="hero-tag">BLE &amp; Barcode Workflows</span>
      </div>
    </div>
  </header>

  <!-- Overview -->
  <section class="section section--overview" id="overview">
    <h2>Overview</h2>
    <p class="card card--soft">
      Full-stack web & mobile developer focused on building real,
      production-ready systems that solve operational problems. I specialize in
      Angular, mobile apps (Ionic/Capacitor), Python backends, and PostgreSQL;
      turning complex business logic, hardware integrations, and real-world
      workflows into clean, reliable software.
      <br /><br />
      I thrive in environments where problem-solving, ownership, and pragmatic
      engineering matter. I learn fast, work well with cross-functional teams,
      and consistently deliver solutions that make people's day-to-day work
      easier.
    </p>
  </section>

  <!-- Skills -->
  <section class="section section--skills" id="skills">
    <h2>Skills</h2>
    <ol class="skills-list">
      <li class="card skill-card">
        <h3>Full-Stack Web &amp; Mobile Development</h3>
        <div class="card-padding">
          <ul class="sublist">
            <li>Angular (Material, standalone components, signals)</li>
            <li>Ionic / Capacitor mobile apps (Android &amp; iOS)</li>
            <li>TypeScript, RxJS, state management, routing</li>
            <li>PWA development, responsive UI, accessibility</li>
          </ul>
        </div>
      </li>

      <li class="card skill-card">
        <h3>Backend Engineering &amp; APIs</h3>
        <div class="card-padding">
          <ul class="sublist">
            <li>Python (web2py, REST API design, automation)</li>
            <li>PostgreSQL (schema design, optimization, complex queries)</li>
            <li>Data modeling for operational workflows</li>
            <li>Authentication, authorization, and session security</li>
          </ul>
        </div>
      </li>

      <li class="card skill-card">
        <h3>Hardware &amp; Real-World Integrations</h3>
        <div class="card-padding">
          <ul class="sublist">
            <li>Bluetooth LE (BLE) communication &amp; services</li>
            <li>Barcode scanning (Honeywell, Zebra, CT series)</li>
            <li>Device sensors, NFC, background services</li>
            <li>Offline-first workflows &amp; sync logic</li>
          </ul>
        </div>
      </li>

      <li class="card skill-card">
        <h3>Software Architecture &amp; DevOps</h3>
        <div class="card-padding">
          <ul class="sublist">
            <li>Application architecture &amp; refactoring</li>
            <li>CI/CD pipelines (GitHub Actions, Firebase Deploy)</li>
            <li>Firebase Cloud Functions &amp; FCM notifications</li>
            <li>Docker, Linux servers, Ubuntu environment management</li>
          </ul>
        </div>
      </li>

      <li class="card skill-card">
        <h3>Leadership &amp; Collaboration</h3>
        <div class="card-padding">
          <ul class="sublist">
            <li>Clear technical communication</li>
            <li>Strong ownership of projects end-to-end</li>
            <li>Team mentorship &amp; knowledge sharing</li>
            <li>Empathy, dependability, and long-term vision</li>
          </ul>
        </div>
      </li>

      <li class="card skill-card">
        <h3>Operational Problem-Solving &amp; Data</h3>
        <div class="card-padding">
          <ul class="sublist">
            <li>
              Translating messy real-world workflows into reliable software
            </li>
            <li>
              Debugging complex production issues across frontend, backend, and
              DB
            </li>
            <li>
              Designing reports and dashboards for ops, inventory, and
              forecasting
            </li>
            <li>
              Data-driven iteration: logs, metrics, and user feedback loops
            </li>
          </ul>
        </div>
      </li>
    </ol>
  </section>

  <!-- Work Experience -->
  <section class="section section--experience" id="work-experience">
    <h2>Work Experience</h2>

    <article class="card experience-entry">
      <div class="card-padding">
        <h3 class="role">Senior Full-Stack Developer</h3>
        <p class="company">SecuraStock · Cleveland, OH</p>
        <p class="dates">2021 – Present</p>
        <ul class="responsibilities">
          <li>
            Lead development of an inventory &amp; parts-management web app
            using Angular 15, Material, and PostgreSQL for industrial clients.
          </li>
          <li>
            Architected and deployed a hybrid mobile app (Android/iOS) using
            Capacitor for field-technician workflows (barcode scanning, BLE,
            offline sync).
          </li>
          <li>
            Built backend services with Python (web2py) and REST APIs, optimized
            database schema for ~1M records, improved query performance by 45%.
          </li>
          <li>
            Mentored junior developers and introduced Angular Signals &amp;
            standalone components to streamline the codebase.
          </li>
        </ul>
        <a
          class="company-card"
          href="https://www.securastock.com"
          target="_blank"
          rel="noopener"
          aria-label="SecuraStock website"
        >
          <div class="company-logo">
            <img src="/assets/securastock.png" alt="SecuraStock logo" />
          </div>
        </a>
      </div>
    </article>

    <article class="card experience-entry">
      <div class="card-padding">
        <h3 class="role">Full-Stack Developer</h3>
        <p class="company">Property Panorama · Cleveland, OH</p>
        <p class="dates">2017 – 2021</p>
        <ul class="responsibilities">
          <li>
            Served as lead developer for multiple revenue-driving products,
            including BOOST, Agent Profile, and enhancements to the flagship
            InstaView virtual tour system used on numerous Realtor.com listings.
          </li>
          <li>
            Completely redesigned and rebuilt Property Panorama's public-facing
            marketing website, modernizing the UI/UX, improving SEO, and
            simplifying marketing for real estate agents and brokerages.
          </li>
          <li>
            Maintained and improved InstaView's mobile experience, implemented
            UI updates, and optimized the tour rendering pipeline for better
            load times and device compatibility.
          </li>
          <li>
            Designed the database schema and backend services for BOOST, a
            dynamic ad-creation tool that imported MLS listing data and
            generated multiple image resolutions for various ad placements.
          </li>
          <li>
            Built BOOST's frontend architecture using Webpack, Babel, and modern
            JavaScript; implemented Web Worker-based image compression to
            improve performance for large photo sets.
          </li>
          <li>
            Developed Agent Profile, a customizable agent websites platform
            powered by MLS-synced data (listings, photos, agent details) and
            structured data for SEO enhancement; owned both the frontend and
            backend API layers.
          </li>
          <li>
            Collaborated closely with product and operations to improve user
            experience across the entire web portal, taking long-term ownership
            of maintainability and architectural consistency.
          </li>
        </ul>

        <a
          class="company-card"
          href="https://www.propertypanorama.com"
          target="_blank"
          rel="noopener"
          aria-label="Property Panorama website"
        >
          <div class="company-logo">
            <img
              src="/assets/propertypanorama.png"
              alt="Property Panorama logo"
            />
          </div>
        </a>
      </div>
    </article>

    <article class="card experience-entry">
      <div class="card-padding">
        <h3 class="role">Database Developer (Professional Apprentice)</h3>
        <p class="company">Sherwin-Williams · Cleveland, OH</p>
        <p class="dates">2015 – 2017</p>
        <ul class="responsibilities">
          <li>
            Designed and maintained Oracle database schemas to support internal
            Oracle APEX applications used by the IT organization.
          </li>
          <li>
            Built a KPI tracking application integrated with the team's
            ticketing system, giving managers clear visibility into workload and
            performance for a 50-person support team.
          </li>
          <li>
            Developed a monitoring dashboard for Hyperion multidimensional
            databases, surfacing cube size and key metrics used in financial
            forecasting workflows.
          </li>
          <li>
            Integrated both applications with Sherwin-Williams Active Directory
            for authentication and access control, and implemented JavaScript
            enhancements to streamline form input and data entry.
          </li>
        </ul>
        <a
          class="company-card"
          href="https://www.sherwin-williams.com"
          target="_blank"
          rel="noopener"
          aria-label="Sherwin-Williams website"
        >
          <div class="company-logo">
            <img src="/assets/sherwin.png" alt="Sherwin-Williams logo" />
          </div>
        </a>
      </div>
    </article>
  </section>

  <!-- Contact -->
  <section class="section section--contact">
    <h2>Connect</h2>
    <form id="contact" [formGroup]="connect" (ngSubmit)="submitForm($event)">
      <mat-form-field appearance="fill" class="grid-span-2">
        <mat-label>Name*</mat-label>
        <input
          matInput
          formControlName="name"
          [disabled]="inputDisabled"
          type="text"
          name="name"
        />
      </mat-form-field>

      <mat-form-field appearance="fill">
        <mat-label>Email*</mat-label>
        <input
          matInput
          formControlName="email"
          [disabled]="inputDisabled"
          type="email"
          name="email"
        />
      </mat-form-field>

      <mat-form-field appearance="fill">
        <mat-label>Phone (optional)</mat-label>
        <input
          matInput
          formControlName="phone"
          [disabled]="inputDisabled"
          type="text"
          name="phone"
        />
      </mat-form-field>

      <mat-form-field appearance="fill" class="grid-span-2">
        <mat-label>Message*</mat-label>
        <textarea
          matInput
          formControlName="message"
          [disabled]="inputDisabled"
          name="message"
          rows="4"
        ></textarea>
      </mat-form-field>

      <button
        mat-raised-button
        color="primary"
        class="grid-span-2"
        type="submit"
        [disabled]="inputDisabled || !connect.valid"
      >
        Submit
      </button>
    </form>
  </section>

  <footer class="site-footer">
    <div class="footer-inner">
      <div class="footer-brand">
        <h2>Let's build something useful.</h2>
        <p>
          I design and ship systems that solve real operational problems — from
          inventory and tooling to field workflows and automation.
        </p>
      </div>

      <nav class="footer-nav" aria-label="Footer navigation">
        <h3>Navigate</h3>
        <ul>
          <li><a href="#overview">Overview</a></li>
          <li><a href="#skills">Skills</a></li>
          <li><a href="#work-experience">Experience</a></li>
          <li><a href="#contact">Connect</a></li>
        </ul>
      </nav>

      <div class="footer-social">
        <h3>Links</h3>
        <div class="footer-social-icons">
          <a
            href="https://www.linkedin.com/in/michael-artman/"
            aria-label="LinkedIn"
            target="_blank"
            rel="noopener"
          >
            <mat-icon>link</mat-icon>
          </a>
          <a
            href="https://github.com/kixes"
            aria-label="GitHub"
            target="_blank"
            rel="noopener"
          >
            <mat-icon>code</mat-icon>
          </a>
          <a
            href="https://stackoverflow.com/cv/artman"
            aria-label="StackOverflow"
            target="_blank"
            rel="noopener"
          >
            <mat-icon>help_outline</mat-icon>
          </a>
        </div>
      </div>
    </div>

    <div class="footer-meta">
      <span>&copy; 2025 Michael Artman</span>
      <span class="footer-tech">Built with Angular &amp; Material</span>
    </div>
  </footer>

  <a
    mat-fab
    color="primary"
    class="home-fab"
    routerLink="/"
    aria-label="Back to home"
  >
    <mat-icon>home</mat-icon>
  </a>
</main>
